/**
 * Requirements Index Component
 * 
 * Level 1 UI for micro-coding: table/list view of all requirements with status,
 * iteration count, and progress. Real-time updates via WebSocket subscription.
 * 
 * Click a row to open the Micro-Unit Detail tab (Level 2).
 * 
 * Lightweight: only receives { reqId, status, iteration } payloads,
 * not full code content. Handles hundreds of requirements efficiently.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '@/components/ui';
import { websocketService } from '../../services/websocket-service';
import { api } from '../../services/api';
import type { StreamHandlers } from '../../services/streaming';

// =====================================================================
// Types
// =====================================================================

export type MicroUnitStatus =
  | 'pending'
  | 'writing'
  | 'compiling'
  | 'testing'
  | 'analyzing'
  | 'evaluating'
  | 'fixing'
  | 'complete'
  | 'escalated';

export interface RequirementRow {
  reqId: string;
  requirementText: string;
  status: MicroUnitStatus;
  iteration: number;
  maxIterations: number;
  phase?: string;
  message?: string;
  lastUpdated?: string;
}

interface RequirementsIndexProps {
  /** Parent project ID (for API path) */
  projectId: string;
  /** Coding project ID (micro-unit namespace in Redis) */
  codingProjectId: string;
  onRequirementClick?: (reqId: string) => void;
  /** Whether this coding project is linked to a conversation in develop mode */
  isAIEditing?: boolean;
  /** Whether the linking operation is in progress */
  isLinking?: boolean;
  /** Trigger linking: creates/finds conversation and links coding project */
  onEnableAIEdit?: () => void;
  /** Unlink coding project from conversation */
  onDisableAIEdit?: () => void;
  className?: string;
}

// =====================================================================
// Status Badge
// =====================================================================

const STATUS_CONFIG: Record<MicroUnitStatus, { label: string; className: string; icon: string }> = {
  pending:    { label: 'Pending',    className: 'bg-muted text-muted-foreground',        icon: '○' },
  writing:    { label: 'Writing',    className: 'bg-info/10 text-info',                  icon: '✎' },
  compiling:  { label: 'Compiling',  className: 'bg-info/10 text-info',                  icon: '⚙' },
  testing:    { label: 'Testing',    className: 'bg-info/10 text-info',                  icon: '▶' },
  analyzing:  { label: 'Analyzing',  className: 'bg-info/10 text-info',                  icon: '◉' },
  evaluating: { label: 'Evaluating', className: 'bg-warning/10 text-warning',            icon: '⚖' },
  fixing:     { label: 'Fixing',     className: 'bg-warning/10 text-warning',            icon: '⟳' },
  complete:   { label: 'Complete',   className: 'bg-success/10 text-success',            icon: '✓' },
  escalated:  { label: 'Escalated',  className: 'bg-destructive/10 text-destructive',    icon: '!' },
};

function StatusBadge({ status }: { status: MicroUnitStatus }) {
  const config = STATUS_CONFIG[status];
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${config.className}`}>
      <span>{config.icon}</span>
      {config.label}
    </span>
  );
}

// =====================================================================
// Progress Bar
// =====================================================================

function ProgressBar({ completed, total }: { completed: number; total: number }) {
  const percent = total > 0 ? Math.round((completed / total) * 100) : 0;
  return (
    <div className="flex items-center gap-3">
      <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
        {/* eslint-disable no-restricted-syntax -- dynamic width requires inline style for progress bar */}
        <div
          className="h-full rounded-full bg-success transition-all duration-300"
          style={{ width: `${percent}%` }}
        />
        {/* eslint-enable no-restricted-syntax */}
      </div>
      <span className="text-sm font-medium text-muted-foreground whitespace-nowrap">
        {completed}/{total} ({percent}%)
      </span>
    </div>
  );
}

// =====================================================================
// Main Component
// =====================================================================

export function RequirementsIndex({
  projectId,
  codingProjectId,
  onRequirementClick,
  isAIEditing,
  isLinking,
  onEnableAIEdit,
  onDisableAIEdit,
  className,
}: RequirementsIndexProps) {
  const [requirements, setRequirements] = useState<RequirementRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | MicroUnitStatus>('all');
  const subscriptionRef = useRef(false);

  // Fetch requirements from API on mount
  useEffect(() => {
    if (!projectId || !codingProjectId) return;

    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.get(`/projects/${projectId}/coding-projects/${codingProjectId}/micro-units`);
        if (!cancelled) {
          setRequirements(response.data.data || []);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          const msg = err instanceof Error ? err.message : 'Failed to load requirements';
          setError(msg);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [projectId, codingProjectId]);

  // Subscribe to project-level status updates on mount
  useEffect(() => {
    if (!codingProjectId || subscriptionRef.current) return;

    websocketService.subscribeMicroProject(codingProjectId);
    subscriptionRef.current = true;

    return () => {
      websocketService.unsubscribeMicroProject(codingProjectId);
      subscriptionRef.current = false;
    };
  }, [codingProjectId]);

  // Handle WebSocket micro_unit_progress events
  const handleMicroUnitUpdate = useCallback((data: {
    reqId?: string;
    phase?: string;
    iteration?: number;
    message?: string;
    success?: boolean;
  }) => {
    if (!data.reqId) return;

    setRequirements(prev => {
      const exists = prev.some(req => req.reqId === data.reqId);

      // Derive status from phase
      let status: MicroUnitStatus = 'pending';
      if (data.phase === 'fill') status = data.iteration && data.iteration > 0 ? 'fixing' : 'writing';
      else if (data.phase === 'compile') status = data.success === false ? 'fixing' : 'compiling';
      else if (data.phase === 'test') status = data.success === false ? 'fixing' : 'testing';
      else if (data.phase === 'analyze') status = data.success === false ? 'fixing' : 'analyzing';
      else if (data.phase === 'evaluate') status = 'evaluating';

      if (!exists) {
        // New requirement added via expand-scope -- insert a row dynamically
        return [...prev, {
          reqId: data.reqId!,
          requirementText: data.message || `Requirement ${data.reqId}`,
          status,
          iteration: data.iteration ?? 0,
          maxIterations: 5,
          phase: data.phase,
          message: data.message,
          lastUpdated: new Date().toISOString(),
        }];
      }

      return prev.map(req => {
        if (req.reqId !== data.reqId) return req;
        return {
          ...req,
          status: status !== 'pending' ? status : req.status,
          iteration: data.iteration ?? req.iteration,
          phase: data.phase ?? req.phase,
          message: data.message ?? req.message,
          lastUpdated: new Date().toISOString(),
        };
      });
    });
  }, []);

  const handleMicroUnitComplete = useCallback((data: { reqId?: string }) => {
    if (!data.reqId) return;
    setRequirements(prev => prev.map(req =>
      req.reqId === data.reqId
        ? { ...req, status: 'complete' as MicroUnitStatus, lastUpdated: new Date().toISOString() }
        : req
    ));
  }, []);

  const handleMicroUnitPaused = useCallback((data: { reqId?: string; reason?: string }) => {
    if (!data.reqId) return;
    setRequirements(prev => prev.map(req =>
      req.reqId === data.reqId
        ? { ...req, status: 'escalated' as MicroUnitStatus, lastUpdated: new Date().toISOString() }
        : req
    ));
  }, []);

  // Register WebSocket handlers for the job
  useEffect(() => {
    // These handlers are called by the WebSocket service when micro_unit events arrive
    // We register them as job handlers since micro-coding events include jobId
    const handlers = {
      onMicroUnitProgress: handleMicroUnitUpdate,
      onMicroUnitComplete: handleMicroUnitComplete,
      onMicroUnitPaused: handleMicroUnitPaused,
    };

    // Register on projectId as the job handler key
    websocketService.registerJobHandlers(codingProjectId, handlers as StreamHandlers);

    return () => {
      websocketService.unregisterJobHandlers(codingProjectId);
    };
  }, [codingProjectId, handleMicroUnitUpdate, handleMicroUnitComplete, handleMicroUnitPaused]);

  // Compute summary stats
  const completedCount = requirements.filter(r => r.status === 'complete').length;
  const escalatedCount = requirements.filter(r => r.status === 'escalated').length;
  const activeCount = requirements.filter(r => !['pending', 'complete', 'escalated'].includes(r.status)).length;
  const totalCount = requirements.length;

  // Filter requirements
  const filteredReqs = filter === 'all'
    ? requirements
    : requirements.filter(r => r.status === filter);

  if (loading) {
    return (
      <div className={`flex items-center justify-center py-12 ${className || ''}`}>
        <p className="text-sm text-muted-foreground">Loading requirements...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flex items-center justify-center py-12 ${className || ''}`}>
        <p className="text-sm text-destructive">{error}</p>
      </div>
    );
  }

  if (totalCount === 0) {
    return (
      <div className={`flex flex-col items-center justify-center py-12 gap-3 ${className || ''}`}>
        <p className="text-sm text-muted-foreground">No manifest yet.</p>
        {isAIEditing ? (
          <p className="text-xs text-muted-foreground">
            Development linked. Send a message in the chat to generate requirements.
          </p>
        ) : (
          <>
            <p className="text-xs text-muted-foreground">Link this project to a conversation to begin.</p>
            <Button
              onClick={onEnableAIEdit}
              disabled={isLinking}
              variant="info"
              size="sm"
            >
              {isLinking ? 'Linking...' : 'Start AI Development'}
            </Button>
          </>
        )}
      </div>
    );
  }

  return (
    <div className={`flex flex-col gap-4 ${className || ''}`}>
      {/* Header with progress */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Requirements</h3>
          <p className="text-sm text-muted-foreground mt-0.5">
            {activeCount > 0 ? `${activeCount} in progress` : completedCount === totalCount ? 'All complete' : `${totalCount - completedCount} remaining`}
            {escalatedCount > 0 && ` · ${escalatedCount} escalated`}
          </p>
        </div>
        <div>
          {isAIEditing ? (
            <Button
              onClick={onDisableAIEdit}
              variant="secondary"
              size="sm"
            >
              Stop Development
            </Button>
          ) : (
            <Button
              onClick={onEnableAIEdit}
              disabled={isLinking}
              variant="info"
              size="sm"
            >
              {isLinking ? 'Linking...' : 'Start AI Development'}
            </Button>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <ProgressBar completed={completedCount} total={totalCount} />

      {/* Filter tabs */}
      <div className="flex gap-1.5 flex-wrap">
        {(['all', 'pending', 'writing', 'compiling', 'testing', 'analyzing', 'complete', 'escalated'] as const).map(f => {
          const count = f === 'all'
            ? totalCount
            : requirements.filter(r => r.status === f).length;
          if (f !== 'all' && count === 0) return null;
          return (
            <Button
              key={f}
              variant={filter === f ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setFilter(f)}
              className="text-xs"
            >
              {f === 'all' ? 'All' : STATUS_CONFIG[f].label} ({count})
            </Button>
          );
        })}
      </div>

      {/* Requirements table */}
      <div className="border border-border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/30">
              <th className="text-left px-3 py-2 font-medium text-muted-foreground">ID</th>
              <th className="text-left px-3 py-2 font-medium text-muted-foreground">Requirement</th>
              <th className="text-left px-3 py-2 font-medium text-muted-foreground">Status</th>
              <th className="text-right px-3 py-2 font-medium text-muted-foreground">Iter</th>
              <th className="text-left px-3 py-2 font-medium text-muted-foreground">Message</th>
            </tr>
          </thead>
          <tbody>
            {filteredReqs.map(req => (
              <tr
                key={req.reqId}
                className="border-b border-border last:border-b-0 hover:bg-muted/20 cursor-pointer transition-colors"
                onClick={() => onRequirementClick?.(req.reqId)}
              >
                <td className="px-3 py-2 font-mono text-xs text-foreground whitespace-nowrap">
                  {req.reqId}
                </td>
                <td className="px-3 py-2 text-foreground max-w-[300px] truncate">
                  {req.requirementText}
                </td>
                <td className="px-3 py-2">
                  <StatusBadge status={req.status} />
                </td>
                <td className="px-3 py-2 text-right font-mono text-xs text-muted-foreground">
                  {req.iteration}/{req.maxIterations}
                </td>
                <td className="px-3 py-2 text-xs text-muted-foreground max-w-[250px] truncate">
                  {req.message || '—'}
                </td>
              </tr>
            ))}
            {filteredReqs.length === 0 && (
              <tr>
                <td colSpan={5} className="px-3 py-8 text-center text-muted-foreground">
                  No requirements match the filter.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default RequirementsIndex;
